import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Target, Copy, ArrowDownToLine } from "lucide-react";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart, ReferenceLine,
} from "recharts";
import { fmt$ } from "@/lib/format";
import { incomeBasis, solveIncomeTarget } from "@/lib/incomePlanner";

// AI Income Target Planner — instantly solves the lump-sum or monthly DCA needed for a monthly
// income target (current yield on market value), can push either amount into the portfolio form,
// and asks Claude for a plain-English explanation on demand.
export function IncomeTargetCard({ portfolioId, summary, drip, initial, seedVersion, onChange, onApplyLumpSum, onApplyMonthly, applying }) {
  const [target, setTarget] = useState(initial?.target ?? 1500);
  const [years, setYears] = useState(initial?.years ?? 10);
  const [growth, setGrowth] = useState(initial?.growth ?? 3.0);
  const [narrative, setNarrative] = useState(initial?.narrative || "");
  const [narrativeFor, setNarrativeFor] = useState(initial?.narrative_for || null);
  const [busy, setBusy] = useState(false);

  // Re-seed only when a different portfolio (or its saved workspace) loads — not on every parent render.
  useEffect(() => {
    setTarget(initial?.target ?? 1500);
    setYears(initial?.years ?? 10);
    setGrowth(initial?.growth ?? 3.0);
    setNarrative(initial?.narrative || "");
    setNarrativeFor(initial?.narrative_for || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, seedVersion]);

  const { currentYield } = incomeBasis(summary);
  const yoc = Number(summary?.yield_on_cost_pct || 0);
  const solved = useMemo(
    () => solveIncomeTarget({ monthlyTarget: target, years, growthPct: growth, currentYieldPct: currentYield, drip: !!drip }),
    [target, years, growth, currentYield, drip],
  );
  const inputsKey = `${Number(target)}|${Number(years)}|${Number(growth)}`;
  const narrativeStale = narrative && narrativeFor !== inputsKey;

  // Persist inputs + computed numbers with the portfolio (debounced).
  useEffect(() => {
    if (!onChange || !solved) return;
    const t = setTimeout(() => onChange({
      target: Number(target), years: Number(years), growth: Number(growth),
      narrative, narrative_for: narrativeFor,
      results: {
        current_yield_pct: currentYield,
        lump_sum_now: solved.lumpNow,
        lump_sum_patient: solved.lumpPatient,
        dca_monthly: solved.dca.monthly,
        dca_total_contributed: solved.dca.totalContributed,
        dca_terminal_value: solved.dca.terminalValue,
      },
    }), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey, narrative, narrativeFor, currentYield]);

  const explain = async () => {
    if (!portfolioId) {
      toast.error("Save the portfolio first — the AI explanation runs on saved portfolios only");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/portfolios/${portfolioId}/income-target`, {
        monthly_target: Number(target),
        dca_years: Number(years),
        assumed_yield_growth_pct: Number(growth),
      });
      setNarrative(res.data.narrative || "");
      setNarrativeFor(inputsKey);
      if (!res.data.narrative) toast.error("AI explanation unavailable right now — numbers above are still valid");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI explanation failed");
    } finally { setBusy(false); }
  };

  const copyNarrative = async () => {
    try {
      await navigator.clipboard.writeText(narrative);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Card className="border-secondary/40 bg-secondary/[0.03]" data-testid="income-target-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
            <Target className="w-3.5 h-3.5" /> AI Income Target Planner
          </div>
          <h3 className="serif text-2xl mt-2">How much do I need to invest?</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Set a monthly dividend income goal. New money is priced at this portfolio&apos;s <b>current yield</b> on today&apos;s
            market value ({currentYield.toFixed(2)}%) — not its historic yield-on-cost ({yoc.toFixed(2)}%).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <PlannerInput id="it-target" label="Monthly income goal ($)" prefix="$" value={target} onChange={setTarget} min={1} max={1000000} step="any" testId="income-target-input" />
          <PlannerInput id="it-years" label="DCA horizon (years)" value={years} onChange={setYears} min={1} max={40} step={1} testId="income-target-years" />
          <PlannerInput id="it-growth" label="Assumed dividend growth (%/yr)" value={growth} onChange={setGrowth} min={0} max={25} step="any" testId="income-target-growth" />
        </div>

        {!solved ? (
          <div className="text-xs text-destructive" data-testid="income-target-error">
            {currentYield > 0 ? "Enter a target above $0 and a horizon of at least 1 year." : "This portfolio has no dividend yield — add dividend-paying holdings."}
          </div>
        ) : (
          <div className="space-y-4" data-testid="income-target-result">
            <div className="flex flex-wrap gap-2 text-[11px]">
              {[`Current yield ${currentYield.toFixed(2)}%`, `YoC ${yoc.toFixed(2)}%`, `Div growth ${Number(growth)}%/yr`, `Horizon ${Number(years)}y`, `DRIP ${drip ? "ON" : "OFF"}`].map((t) => (
                <span key={t} className="mono px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">{t}</span>
              ))}
            </div>

            <p className="text-sm leading-relaxed" data-testid="income-target-headline">
              At your portfolio&apos;s <b>{currentYield.toFixed(2)}% current yield</b>, {fmt$(Number(target) * 12)}/year of dividends
              requires about <b className="text-primary">{fmt$(solved.lumpNow)}</b> invested today.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SolveBox
                tone="primary" title="Lump-sum · today" amount={fmt$(solved.lumpNow)} testId="income-target-lump-now"
                note={`Invest today and earn about ${fmt$(Number(target))}/mo right away at ${currentYield.toFixed(2)}% yield.`}
                action={{ label: "Use as initial investment", onClick: () => onApplyLumpSum?.(solved.lumpNow), testId: "income-target-apply-lump" }}
                applying={applying}
              />
              <SolveBox
                tone="secondary" title="Lump-sum · patient" amount={fmt$(solved.lumpPatient)} testId="income-target-lump-patient"
                note={`Smaller amount today; reaches the goal in ${Number(years)}y via ${Number(growth)}% dividend growth${drip ? " + DRIP" : ""}.`}
                action={{ label: "Use as initial investment", onClick: () => onApplyLumpSum?.(solved.lumpPatient), testId: "income-target-apply-patient" }}
                applying={applying}
              />
              <SolveBox
                tone="muted" title="Or DCA" amount={<>{fmt$(solved.dca.monthly)}<span className="mono text-base text-muted-foreground"> /mo</span></>} testId="income-target-dca"
                note={`For ${Number(years)} years · contribute ${fmt$(solved.dca.totalContributed)}, projected value ${fmt$(solved.dca.terminalValue)}.`}
                action={{ label: "Use as monthly contribution", onClick: () => onApplyMonthly?.(solved.dca.monthly), testId: "income-target-apply-dca" }}
                applying={applying}
              />
            </div>

            {solved.dca.projection.length > 1 && (
              <IncomeProjectionChart
                projection={solved.dca.projection}
                monthlyTarget={Number(target)}
                hitMonth={solved.dca.hitMonth}
                years={Number(years)}
              />
            )}

            <div className="rounded-md border border-secondary/20 bg-secondary/[0.03] p-4" data-testid="income-target-narrative-card">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> AI explanation
                  {narrativeStale && <span className="normal-case tracking-normal text-muted-foreground">· written for a previous target</span>}
                </div>
                <div className="flex items-center gap-2">
                  {narrative && (
                    <button onClick={copyNarrative} className="text-muted-foreground hover:text-primary transition-colors" aria-label="Copy narrative" data-testid="income-target-copy">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={explain} disabled={busy || !portfolioId} data-testid="income-target-solve">
                    {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Explaining…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />{narrative ? "Re-explain" : "Explain with AI"}</>}
                  </Button>
                </div>
              </div>
              {narrative ? (
                <div className={`text-sm leading-relaxed whitespace-pre-wrap ${narrativeStale ? "opacity-60" : ""}`} data-testid="income-target-narrative">{narrative}</div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {portfolioId ? "Get a plain-English walkthrough of these numbers, assumptions and risks (uses 1 AI action)." : "Save this portfolio to unlock the AI explanation."}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlannerInput({ id, label, prefix, value, onChange, min, max, step, testId }) {
  return (
    <div>
      <Label htmlFor={id} className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="relative mt-1">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">{prefix}</span>}
        <Input id={id} type="number" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(e.target.value)} className={`mono ${prefix ? "pl-7" : ""}`} data-testid={testId} />
      </div>
    </div>
  );
}

function SolveBox({ tone, title, amount, note, action, applying, testId }) {
  const box = tone === "primary" ? "border-primary/40 bg-primary/[0.04]" : tone === "secondary" ? "border-secondary/40 bg-secondary/[0.05]" : "border-border bg-card";
  const text = tone === "primary" ? "text-primary" : tone === "secondary" ? "text-secondary" : "";
  return (
    <div className={`rounded-md border px-4 py-4 flex flex-col ${box}`} data-testid={testId}>
      <div className={`mono text-[10px] uppercase tracking-widest ${text || "text-muted-foreground"}`}>{title}</div>
      <div className={`serif text-2xl xl:text-3xl mt-2 whitespace-nowrap tabular-nums ${text}`} data-testid={`${testId}-amount`}>{amount}</div>
      <div className="mono text-[11px] text-muted-foreground mt-1 flex-1">{note}</div>
      <Button size="sm" variant="outline" className="mt-3 rounded-full w-full h-auto min-h-8 py-1.5 whitespace-normal text-xs leading-tight" onClick={action.onClick} disabled={applying} data-testid={action.testId}>
        <ArrowDownToLine className="w-3.5 h-3.5 mr-1.5 shrink-0" />{action.label}
      </Button>
    </div>
  );
}


// Recharts area chart of monthly dividend income across the DCA horizon,
// with a target reference line and a milestone strip below.
function IncomeProjectionChart({ projection, monthlyTarget, hitMonth, years }) {
  const data = projection.map((p) => ({
    year: p.year,
    income: p.monthly_income,
    value: p.portfolio_value,
    invested: p.invested,
    yield_pct: p.yield_pct,
  }));
  const hitYear = hitMonth != null ? Math.round((hitMonth / 12) * 100) / 100 : null;
  return (
    <div className="rounded-md border border-border bg-card px-4 py-4" data-testid="income-target-chart">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-secondary">DCA projection</div>
          <div className="serif text-lg">Monthly dividend income · {years}-year horizon</div>
        </div>
        {hitYear != null && (
          <span className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            Hits target in year {hitYear}
          </span>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0F3D32" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#0F3D32" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
            <XAxis dataKey="year" tick={{ fontSize: 10 }} tickFormatter={(v) => `y${Math.round(v)}`} />
            <YAxis
              tick={{ fontSize: 10 }}
              width={55}
              tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`}
            />
            <Tooltip
              contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
              formatter={(v, name) => {
                if (name === "income") return [`$${Number(v).toFixed(2)}/mo`, "Monthly income"];
                return [v, name];
              }}
              labelFormatter={(l) => `Year ${l}`}
            />
            <ReferenceLine
              y={monthlyTarget}
              stroke="#8B7355"
              strokeDasharray="4 4"
              label={{ value: `Target $${monthlyTarget}`, position: "insideTopRight", fontSize: 10, fill: "#8B7355" }}
            />
            <Area type="monotone" dataKey="income" stroke="#0F3D32" strokeWidth={2} fill="url(#incomeFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Milestone strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="projection-milestones">
        {[data[Math.floor(data.length / 4)], data[Math.floor(data.length / 2)], data[Math.floor((data.length * 3) / 4)], data[data.length - 1]]
          .filter(Boolean)
          .map((row, i) => (
            <div key={i} className="rounded-sm border border-border bg-muted/30 px-2 py-1.5">
              <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Year {Math.round(row.year)}</div>
              <div className="serif text-sm text-primary">${row.income.toFixed(0)}/mo</div>
              <div className="mono text-[9px] text-muted-foreground">value {fmt$(row.value)}</div>
            </div>
          ))}
      </div>
      <p className="text-[10px] text-muted-foreground italic mt-3">
        Projection assumes contributions compound at the portfolio&apos;s historic CAGR while dividend yield grows at the rate you set.
        Real returns will vary — treat this as a planning starting point, not a promise.
      </p>
    </div>
  );
}
